import { useState } from "react";
import { ShieldCheck, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const carriers = [
  {
    name: "Colonial Life",
    tagline: "Supplemental & Group Life",
    logo: "/carriers/colonial-life.png",
    blurb: "Through Colonial Life we offer group term life insurance, accident coverage, critical illness, hospital confinement, and cancer plans. These voluntary benefits are employee paid, so you can strengthen your benefits package at no cost to your business. Our agency has paid out over $650,000 in Colonial Life claims to our clients this year alone.",
  },
  {
    name: "Delta Dental of MN",
    tagline: "Dental Insurance",
    logo: "/carriers/delta-dental.png",
    blurb: "Delta Dental of Minnesota gives your team access to one of the largest dental networks in the region. We offer group dental plans covering preventive care, basic services, and major work with flexible plan designs to fit any budget.",
  },
  {
    name: "VSP Vision",
    tagline: "Vision Insurance",
    logo: "/carriers/vsp-vision.png",
    blurb: "With VSP Vision we provide group vision plans covering annual eye exams, frames, lenses, and contacts. VSP has the largest vision network in the country, so your employees can keep seeing the doctors they trust.",
  },
  {
    name: "Unum",
    tagline: "Disability & Life",
    logo: "/carriers/unum.png",
    blurb: "Through Unum we offer short term disability, long term disability, and group life insurance. These plans protect your employees' paychecks when illness or injury keeps them out of work, giving your team real peace of mind.",
  },
  {
    name: "Ameriflex",
    tagline: "HSAs, FSAs & DCAs",
    logo: "/carriers/ameriflex.jpg",
    blurb: "Ameriflex administers our pre tax spending accounts, including HSAs, FSAs, and Dependent Care Accounts, plus ICHRA administration. These accounts let your employees pay for healthcare and dependent care with pre tax dollars, saving money for both them and your business.",
  },
  {
    name: "United Concordia Dental",
    tagline: "Dental Insurance",
    logo: "/carriers/united-concordia.jpg",
    scale: true,
    blurb: "United Concordia Dental offers competitive group dental plans with strong nationwide networks. We use United Concordia to give employers another high quality dental option, so we can always find the right fit and price for your team.",
  },
];

const CarrierBadge = ({ carrier, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`carrier-${carrier.name}`}
    className="group rounded-2xl border shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all p-6 flex items-center justify-center text-center h-32 bg-white border-gray-100 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#2EC4C4] focus:ring-offset-2"
    aria-label={`Learn more about ${carrier.name}`}
  >
    <img
      src={carrier.logo}
      alt={`${carrier.name} logo`}
      className={`max-w-full object-contain ${carrier.scale ? "max-h-24 scale-125" : "max-h-16"}`}
      loading="lazy"
    />
  </button>
);

export const CarriersSection = () => {
  const [selected, setSelected] = useState(null);

  return (
    <section data-testid="carriers-section" className="py-16 bg-white border-y border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center space-x-2 bg-[#EAF9F9] text-[#2EC4C4] font-semibold text-xs uppercase tracking-wider px-4 py-2 rounded-full mb-4">
            <ShieldCheck size={14} />
            <span>Trusted Carriers</span>
          </div>
          <h2 className="font-bold text-2xl lg:text-3xl text-[#1A2744]" style={{ fontFamily: "'Outfit', sans-serif" }}>
            The Carriers We Partner With
          </h2>
          <p className="text-[#475569] mt-3 max-w-2xl mx-auto">
            We shop the market so you don't have to. Click any carrier to see the plans we offer through them.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {carriers.map((c) => (
            <CarrierBadge key={c.name} carrier={c} onClick={() => setSelected(c)} />
          ))}
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent data-testid="carrier-dialog" className="max-w-md rounded-2xl">
          {selected && (
            <>
              <DialogHeader>
                <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center justify-center h-24 mb-3">
                  <img
                    src={selected.logo}
                    alt={`${selected.name} logo`}
                    className={`max-w-full object-contain ${selected.scale ? "max-h-20 scale-110" : "max-h-14"}`}
                  />
                </div>
                <DialogTitle
                  data-testid="carrier-dialog-title"
                  className="text-xl text-[#1A2744]"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {selected.name}
                </DialogTitle>
                <p className="text-xs font-semibold uppercase tracking-wider text-[#2EC4C4]">{selected.tagline}</p>
              </DialogHeader>
              <DialogDescription data-testid="carrier-dialog-blurb" className="text-[#475569] text-sm leading-relaxed">
                {selected.blurb}
              </DialogDescription>
              <Link
                to="/contact"
                data-testid="carrier-dialog-quote-link"
                onClick={() => setSelected(null)}
                className="inline-flex items-center justify-center space-x-2 bg-[#2EC4C4] hover:bg-[#26ADAD] text-white font-semibold text-sm px-5 py-3 rounded-full transition-colors mt-2"
              >
                <span>Get a Free Quote</span>
                <ArrowRight size={16} />
              </Link>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
